/**
 * @module trends.transformer
 * contain transformer for trends tab related rating measurements
 * As trends might be separated out to it's own section
 * so keep rating measurement entity generated by this trends transformer as trendRatingMeasurement
 */
import { nanoid } from '@reduxjs/toolkit';

const questionDatasetIsWithheld = (dataset) => dataset.count === null;

const withheldFactorTimeframesFrom = (questions) =>
  (questions ?? []).reduce((withheldByFactor, question) => {
    const perTimeframe = withheldByFactor[question.factorId] ?? [];
    question.datasets.forEach((dataset, index) => {
      const withheld = questionDatasetIsWithheld(dataset);
      perTimeframe[index] = index in perTimeframe ? perTimeframe[index] && withheld : withheld;
    });
    return { ...withheldByFactor, [question.factorId]: perTimeframe };
  }, {});

const trendSurveyDatasetFrom = (dataset, index, identities, isSuppressed) => {
  if (identities.group === 'timeframe') {
    return {
      ...identities,
      id: nanoid(8),
      position: index,
      isSuppressed,
      selectedTimeframeIndex: dataset.selectedTimeframeIndex,
      label: dataset.label,
      percentFavorable: dataset.percentFavorable ?? 0,
      percentNeutral: dataset.percentNeutral ?? 0,
      percentUnfavorable: dataset.percentUnfavorable ?? 0,
      scoreAverage: dataset.scoreAverage ?? 0,
      enpsScore: dataset.enpsScore ?? null,
      countFavorable: dataset.countFavorable ?? 0,
      countNeutral: dataset.countNeutral ?? 0,
      countUnfavorable: dataset.countUnfavorable ?? 0,
    };
  }

  return {
    ...identities,
    id: nanoid(8),
    position: index,
    isSuppressed,
    surveys: dataset.ids,
    label: dataset.label,
    percentFavorable: dataset.percentFavorable ?? 0,
    percentNeutral: dataset.percentNeutral ?? 0,
    percentUnfavorable: dataset.percentUnfavorable ?? 0,
    scoreAverage: dataset.scoreAverage ?? 0,
    enpsScore: dataset.enpsScore ?? null,
    countFavorable: dataset.countFavorable ?? 0,
    countNeutral: dataset.countNeutral ?? 0,
    countUnfavorable: dataset.countUnfavorable ?? 0,
  };
};

const trendMeasurementsFrom = (trends, group, type, { canWithhold, withheldFactorTimeframes }) => {
  if (!trends) {
    return [];
  }
  return trends.reduce((measurements, trend) => {
    const isFactor = type === 'factors';
    const identities = {
      categoryId: isFactor ? trend.id : trend.factorId,
      questionId: isFactor ? null : trend.id,
      type,
      group,
    };
    const withheldTimeframes = withheldFactorTimeframes[trend.id] ?? [];
    const ratingMeasurements = trend.datasets.map((dataset, index) => {
      const isSuppressed =
        canWithhold && (isFactor ? Boolean(withheldTimeframes[index]) : questionDatasetIsWithheld(dataset));
      return trendSurveyDatasetFrom(dataset, index, identities, isSuppressed);
    });
    return [...measurements, ...ratingMeasurements];
  }, []);
};

const trendsRatingMeasurementsFrom = (measurements, group) => {
  const { factors, questions } = measurements;
  const canWithhold = group === 'timeframe';
  const withheldFactorTimeframes = canWithhold ? withheldFactorTimeframesFrom(questions) : {};
  const options = { canWithhold, withheldFactorTimeframes };
  return [
    ...trendMeasurementsFrom(factors, group, 'factors', options),
    ...trendMeasurementsFrom(questions, group, 'questions', options),
  ];
};

export { trendsRatingMeasurementsFrom };
